import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, ExternalLink, PenSquare } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { formatDateTime } from "@/utils/formatDate";
import { EmptyState } from "./ui";
import {
  useEopDrafts,
  useEopMessages,
  useEopStatus,
} from "@/features/eop_mail/api/mailbox";
import { DraftCard } from "@/features/eop_mail/components/DraftCard";
import { NewMessageModal } from "@/features/eop_mail/components/NewMessageModal";
import { ThreadPanel } from "@/features/eop_mail/components/ThreadPanel";

/**
 * Korespondencja z ENERGA na karcie przedsięwzięcia.
 *
 * Po co osobno od ekranu skrzynki: pisanie STĄD daje przypisanie pewne i za darmo.
 * Na wspólnym ekranie skrzynki człowiek musi wskazać przedsięwzięcie ręcznie (albo
 * przyjąć propozycję), a tu kontekst jest znany z definicji — to jest ta połowa
 * wartości, dla której w ogóle tagujemy wpływ.
 *
 * Poczta wisi na PRZEDSIĘWZIĘCIU (`connection.project`), nie na pojedynczym
 * przyłączu: operator pisze o sprawie, nie o rekordzie w naszej bazie, więc jeden
 * wątek dotyczy zwykle kilku przyłączy tego samego przedsięwzięcia.
 */
const DIRECTIONS = [
  { key: "", label: "Wszystkie" },
  { key: "incoming", label: "Odebrane" },
  { key: "outgoing", label: "Wysłane" },
] as const;

export function EopMailTab({
  connection,
}: {
  connection: { project: string; project_name?: string; project_short_name?: string };
}) {
  const { data: status } = useEopStatus();
  // Domyślnie wszystkie kierunki: karta przedsięwzięcia to historia SPRAWY,
  // ukrycie własnych odpowiedzi wyglądałoby jak zgubiona poczta.
  const [direction, setDirection] = useState<"" | "incoming" | "outgoing">("");
  const { data: threads, isLoading } = useEopMessages({
    project: connection.project,
    direction: direction || undefined,
    groupByThread: true,
  });
  const { data: drafts } = useEopDrafts("draft");
  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const rows = threads ?? [];
  const current = selected ?? rows[0]?.id ?? null;
  const currentThread = rows.find((r) => r.id === current)?.thread_key;
  // Szkice OTWARTEGO wątku pokazuje już panel po prawej — tutaj zostają tylko te
  // z pozostałych wątków, żeby ta sama kopia robocza nie wisiała dwa razy na ekranie.
  const projectDrafts = (drafts ?? []).filter(
    (d) => d.project === connection.project && d.conversation_id !== currentThread,
  );

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="section-label">
          Poczta EOP — {connection.project_short_name || "przedsięwzięcie"}
        </span>
        <Link
          to={`/power-projects/poczta-eop?project=${connection.project}`}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          cała skrzynka <ExternalLink size={11} />
        </Link>
        <div className="ml-2 inline-flex border border-[var(--color-border)]">
          {DIRECTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => {
                setDirection(item.key);
                setSelected(null);
              }}
              className={`px-2 py-0.5 text-[11px] ${
                direction === item.key
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => setComposing(true)}
          disabled={!status?.enabled}
          title={
            status?.enabled
              ? "Do kogokolwiek w sprawie tego przedsięwzięcia — wiadomość jest od razu do niego przypisana."
              : "Skrzynka eop@ jest wyłączona."
          }
        >
          <PenSquare size={13} className="mr-1" />
          Napisz z eop@
        </Button>
      </div>

      {projectDrafts.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] text-[var(--color-primary)]">
            {projectDrafts.length === 1
              ? "Kopia robocza w innym wątku czeka na wysyłkę."
              : `${projectDrafts.length} kopie robocze w innych wątkach czekają na wysyłkę.`}
          </p>
          {projectDrafts.map((draft) => (
            <DraftCard key={draft.id} draft={draft} />
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState>
          Na tym przedsięwzięciu nie ma jeszcze korespondencji przez eop@. Wpływ
          pojawia się tu po przypisaniu wiadomości na ekranie skrzynki; napisane
          stąd trafia od razu.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[20rem_1fr]">
          <ul className="h-fit divide-y divide-[var(--color-border)] border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
            {rows.map((row) => {
              const waitingOnUs = row.last_move.by === "them";
              const unread = row.thread_unread;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    className={`w-full px-2.5 py-2 text-left ${
                      current === row.id
                        ? "bg-[var(--color-bg-elevated)] shadow-[inset_3px_0_0_var(--color-primary)]"
                        : "hover:bg-[var(--color-bg-elevated)]"
                    } ${unread ? "bg-blue-50/40" : ""}`}
                  >
                    <div className="flex items-baseline gap-2">
                      {unread && (
                        <span
                          className="h-2 w-2 shrink-0 self-center rounded-full bg-blue-600"
                          title="Nieprzeczytane"
                        />
                      )}
                      <span
                        className={`truncate text-[13px] ${
                          unread
                            ? "font-bold text-[var(--color-text-primary)]"
                            : waitingOnUs
                              ? "font-semibold text-[var(--color-text-primary)]"
                              : "text-[var(--color-text-secondary)]"
                        }`}
                      >
                        {row.subject || "(bez tematu)"}
                      </span>
                      {row.thread_action && (
                        <Badge color="red" className="shrink-0 whitespace-nowrap">
                          wymaga akcji
                        </Badge>
                      )}
                      {row.thread_count > 1 && (
                        <span className="shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
                          {row.thread_count}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--color-text-muted)]">
                      <span className={waitingOnUs ? "text-[var(--color-primary)]" : ""}>
                        {waitingOnUs ? (
                          <ArrowDownLeft size={11} className="inline -mt-px mr-0.5" />
                        ) : (
                          <ArrowUpRight size={11} className="inline -mt-px mr-0.5" />
                        )}
                        {waitingOnUs ? row.last_move.who : "my"}
                      </span>
                      <span className="ml-auto font-mono text-[10px]">
                        {formatDateTime(row.received_at)}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {current ? <ThreadPanel key={current} messageId={current} /> : <div />}
        </div>
      )}

      {!status?.enabled && (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          <Badge color="orange">skrzynka wyłączona</Badge> Nowa korespondencja się
          nie zaciąga i nic nie wychodzi.
        </p>
      )}

      <NewMessageModal
        open={composing}
        onClose={() => setComposing(false)}
        defaultProject={connection.project}
      />
    </div>
  );
}
